import HttpError from "./Utils/HttpError";
import { resolveNoContent, resolveWithBody } from "./Utils/utils";
import { usePromisedRequest as promiseRequest } from "../Common/request";
import { ComplexFight, FightQueueStatus, OrganizerComplexFight, UpcomingPublicFightsCollection } from "../Common/Fights/types";
import { Mat } from "../Common/types";
import { ScoreboardType } from "../Scoreboard/Common/types";
import { ComplexBracket } from "../Common/Brackets/types";
import { PartiallyScheduledBracket } from "./schedules";

export type FightsEnumerationStatus = {
    totalFights: number;
    totalEnumeratedFights: number;
};

export const FightsEnumeration = {
    None: 0,
    EachMat: 1,
    GlobalToEntireEvent: 2,
} as const;

export type FightsEnumerationType = (typeof FightsEnumeration)[keyof typeof FightsEnumeration];

export type OrganizerFightsCollection = {
    result: Array<OrganizerComplexFight>; //TODO: It also can have finished fight. Reflect that too. ;(
    fightQueueStatus: FightQueueStatus;
};

export type RelevantFightsForReshuffleResponse = {
    fights: ComplexFight[];
    sourceFightId: number;
};

export type BracketFightsOverviewResponse = {
    categoryName: string;
    fights: ComplexFight[];
    mats: Mat[];
    fightTime: number | null;
    scoreboardType: ScoreboardType;
};

export type TimeCalculatingFightsCollection = {
    startTime: string; // ISO string
    fight: ComplexFight;
    scoreboardType: ScoreboardType;
}[];

export type TotalScheduleTime = {
    totalFights: number;
    totalTimeInSeconds: number;
};

export type FightsGenerationStatus = {
    complexBrackets: ComplexBracket[];
    partiallyScheduledBrackets: PartiallyScheduledBracket[];
};

export type FightsQueuesResult = {
    fightQueueStatuses: Record<number, FightQueueStatus>;
    matsForGivenPage: Mat[];
    //Mat id => fights collection
    fightsCollections: Record<number, TimeCalculatingFightsCollection>;
};

//Source: FightsMoveRequest::QUEUE_PLACES
export type QueueTargetPlace = "BEGINNING" | "END";

//Source: Panel\Fights\Processing\Reshuffling\ReshuffleRequest
export type ReshuffleRequest = {
    parameterId: number;
    firstFightId: number;
    secondFightId: number;
    firstCompetitorId: number;
    secondCompetitorId: number;
};

export default {
    insertBreakBeforeFight(eventId: number, fightId: number, totalSeconds: number): Promise<void> {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/fight-breaks`,
            method: "post",
            json: {
                fightId: fightId,
                totalSeconds: totalSeconds,
            },
        }).then(resolveNoContent("Could not insert break."));
    },
    removeQueueBreak(eventId: number, fightId: number): Promise<void> {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/fight-breaks/${fightId}`,
            method: "delete",
        }).then(resolveNoContent("Could not insert break."));
    },
    finishFight<R extends object>(eventId: number, fightId: number, resultObject: R): Promise<string> {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/fights/${fightId}/results`,
            method: "put",
            json: resultObject,
        }).then(res => {
            if (res.statusCode !== 204) {
                throw new HttpError(`Could not finish fight ${fightId} from event ${eventId}.`, res.statusCode);
            }

            return res.headers.location;
        });
    },
    finishBreak(eventId: number, fightId: number): Promise<string> {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/fights/${fightId}/results`,
            method: "put",
            json: {
                type: "simple",
                winnerCompetitorId: 0,
            },
        }).then(res => {
            if (res.statusCode !== 204) {
                throw Error(`Could not finish fight ${fightId} from event ${eventId}.`);
            }

            return res.headers.location;
        });
    },
    getFightsGenerationStatus(eventId: number): Promise<FightsGenerationStatus> {
        return promiseRequest<FightsGenerationStatus>({
            url: `/api/admin/events/${eventId}/fights-status`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    generateAllFights(eventId: number): Promise<void> {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/generate-all-fights`,
            method: "put",
            json: true,
        }).then(resolveNoContent("Could generate fights."));
    },
    revertFightResult(eventId: number, fightId: number): Promise<void> {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/fights/${fightId}/results`,
            method: "delete",
            json: true,
        }).then(resolveNoContent("Could not revert fight result"));
    },
    reorderFights(eventId: number, fightIds: number[]): Promise<void> {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/fights-order`,
            method: "put",
            json: {
                fightIds: fightIds,
            },
        }).then(resolveNoContent("Could not reorder fights."));
    },
    moveFightsToDifferentMat(eventId: number, fightIds: number[], targetMatId: number, queueTargetPlace: QueueTargetPlace): Promise<void> {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/fights-move`,
            method: "put",
            json: {
                fightIds: fightIds,
                targetMatId: targetMatId,
                queueTargetPlace: queueTargetPlace,
            },
        }).then(resolveNoContent("Could not move fights."));
    },
    getComplexFight(eventId: number, fightId: number): Promise<ComplexFight> {
        return promiseRequest<ComplexFight>({
            url: `/api/admin/events/${eventId}/fights/${fightId}/complex`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    getFightsQueues(eventId: number, matIds: number[] = [], limit = 15, pageSize = 6, pageNumber = 0): Promise<FightsQueuesResult> {
        let url = `/api/admin/events/${eventId}/fights/queues`;

        const queryParams = {};
        if (Array.isArray(matIds) && matIds.length > 0) {
            queryParams["matIds"] = matIds.join(",");
        }

        queryParams["pageSize"] = pageSize;
        queryParams["limit"] = limit;
        queryParams["page"] = pageNumber;

        url += "?" + new URLSearchParams(queryParams).toString();

        return promiseRequest<FightsQueuesResult>({
            url: url,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    getIncomingFights(eventId: number, matId: number, scheduleId?: number): Promise<OrganizerFightsCollection> {
        let url = `/api/admin/events/${eventId}/fights?matId=${matId}`;
        if (scheduleId !== undefined) {
            url += `&scheduleId=${scheduleId}`;
        }
        return promiseRequest<OrganizerFightsCollection>({
            url: url,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    getFinishedFights(eventId: number, matId: number): Promise<OrganizerFightsCollection> {
        return promiseRequest<OrganizerFightsCollection>({
            url: `/api/admin/events/${eventId}/fights?matId=${matId}&finished=1`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    getAllFinishedFights(eventId: number, allSchedules = false): Promise<OrganizerFightsCollection> {
        const enableAllSchedules = allSchedules ? 1 : 0;

        return promiseRequest<OrganizerFightsCollection>({
            url: `/api/admin/events/${eventId}/fights?finished=1&allSchedules=${enableAllSchedules}`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    getNextFightsByFightId(eventId: number, fightId: number, limit?: number): Promise<ComplexFight[]> {
        const base = `/api/admin/events/${eventId}/fights/${fightId}/next`;
        const url = limit !== undefined ? `${base}?limit=${limit}` : base;

        return promiseRequest<ComplexFight[]>({
            url,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    getPublicFightsForMat(eventId: number, matId: number[]): Promise<UpcomingPublicFightsCollection> {
        const matIdsString = matId.join(",");
        return promiseRequest<UpcomingPublicFightsCollection>({
            url: `/api/public/events/${eventId}/fights?matIds=${matIdsString}`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    getPublicFightsQueues(eventId: number, matIds: number[] = [], limit = 15, pageSize = 6, pageNumber = 0): Promise<FightsQueuesResult> {
        let url = `/api/public/events/${eventId}/fights/queues`;

        const queryParams = {};
        if (Array.isArray(matIds) && matIds.length > 0) {
            queryParams["matIds"] = matIds.join(",");
        }

        queryParams["pageSize"] = pageSize;
        queryParams["limit"] = limit;
        queryParams["page"] = pageNumber;

        url += "?" + new URLSearchParams(queryParams).toString();

        return promiseRequest<FightsQueuesResult>({
            url: url,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    getFightsStatusesForDelete(eventId: number) {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/fights-statuses`,
            method: "get",
            json: true,
        }).then(resolveWithBody("Could not get fights statuses."));
    },
    deleteFightsByMat(eventId: number, scheduleId: number, matId: number): Promise<void> {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/schedules/${scheduleId}/mats/${matId}/fights`,
            method: "delete",
            json: true,
        }).then(resolveNoContent());
    },
    deleteFightsByWholeSchedule(eventId: number, scheduleId: number): Promise<void> {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/schedules/${scheduleId}/fights`,
            method: "delete",
            json: true,
        }).then(resolveNoContent());
    },
    deleteFightsForAllSchedules(eventId: number): Promise<void> {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/fights`,
            method: "delete",
            json: true,
        }).then(resolveNoContent());
    },
    deleteFightsByCategory(eventId: number, parameterId: number): Promise<void> {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/categories/${parameterId}/fights`,
            method: "delete",
            json: true,
        }).then(resolveNoContent());
    },
    enumerateFights(eventId: number, enumerationType: FightsEnumerationType): Promise<void> {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/fight-enumerations`,
            method: "post",
            json: {
                enumerationType,
            },
        }).then(resolveNoContent());
    },
    getFightsEnumerationStatus(eventId: number): Promise<FightsEnumerationStatus> {
        return promiseRequest<FightsEnumerationStatus>({
            url: `/api/admin/events/${eventId}/fight-enumerations`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    getApproximateSchedulesTime(eventId: number): Promise<TotalScheduleTime> {
        return promiseRequest<TotalScheduleTime>({
            url: `/api/admin/events/${eventId}/schedules/approximate-total-time`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },

    // Reshuffling competitors between fights
    getRelevantFightsForReshuffle(eventId: number, fightId: number): Promise<RelevantFightsForReshuffleResponse> {
        return promiseRequest<RelevantFightsForReshuffleResponse>({
            url: `/api/events/${eventId}/fights/${fightId}/relevant-fights-for-reshuffle`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    reshuffleCompetitors(
        eventId: number,
        { parameterId, firstFightId, secondFightId, firstCompetitorId, secondCompetitorId }: ReshuffleRequest,
    ): Promise<void> {
        return promiseRequest({
            url: `/api/events/${eventId}/fights/actions/reshuffle-competitors`,
            method: "put",
            json: {
                parameterId,
                firstFightId,
                secondFightId,
                firstCompetitorId,
                secondCompetitorId,
            },
        }).then(resolveNoContent());
    },
    getPublicBracketFightsOverview(eventId: number, parameterId: number): Promise<BracketFightsOverviewResponse> {
        return promiseRequest<BracketFightsOverviewResponse>({
            url: `/api/public/events/${eventId}/brackets/${parameterId}/fights`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
};
